import React, { useContext } from "react";
import compose from "compose-function";
import moment from "moment";

import TableComponent from "../../components/TableComponent";
import CreateItemButton from "../../components/CreateItemButton";

import { useColumns } from "../../hooks/useColumns";
import useUserPermissionsContext from "../../hooks/useUserPermissionsContext";

import {
  getSortOrder,
  sortColumns,
  sortModelToString,
  fetchDataWithOptionsAndFilters,
  exportToFile,
} from "../../utils/utils";

import {
  addHrefToRow,
  changeDeletedToBoolean,
  addValueFormatter,
  padValueWith,
  readDataFromLocalStorage,
  getInitialFilters,
  getDefaultFilterModel,
  generateEntityKeyForLocalStorage,
  addFormatters,
} from "../../utils/table";

import { exportRecords } from "../../dao/common";
import { FILTER_TYPES } from "../../dao/types";

import UserRights from "../../enums/UserRights";
import { ROUTES } from "../../enums/Routes";

import { GlobalContext } from "../../globalContext";
import useEntities from "../../hooks/useEntities";
import { useSortModel } from "../../hooks/useSortModel";
import { API_ROUTES } from "../../enums/api";
import { hardDelete, softDelete } from "../../dao/operations";

const API_ROUTE = API_ROUTES["objects"];

/**
 * Here we need to transform the row data retrieved,
 * in order to make it compatible with @mui/x-data-grid-pro
 */
const transformRows = (rows) => {
  return rows.reduce((acc, row) => {
    return [
      ...acc,
      compose(
        changeDeletedToBoolean("deleted"),
        addHrefToRow({
          field: "id",
          slug: "object",
        })
      )(row),
    ];
  }, []);
};

/**
 * Here we need to transform the column data retrieved,
 * in order to add @mui/x-data-grid-pro formatters
 */
const transformColumns = (columns) => {
  return addFormatters([
    { field: "number", formatter: addValueFormatter("number")(padValueWith(5)) },
  ], columns);
};

export default function ObjectsContainer() {
  const { globalFilters } = useContext(GlobalContext);
  const { sortModel, setSortModel } = useSortModel(FILTER_TYPES.object);

  // Get cached local filter model from local storage
  // with a fallback to default field values
  const cachedLocalFilter = readDataFromLocalStorage(
    generateEntityKeyForLocalStorage(FILTER_TYPES.object, "filterModel")
  ) ?? {
    filterModel: null,
    filterJSON: "",
  };

  // destructure fields from cachedLocalFilter
  const {
    filterModel: cachedLocalFilterModel,
    filterJSON: cachedFilterJSONString,
  } = cachedLocalFilter;

  const initialFilters = getInitialFilters({
    cache: {
      localFilterModel: cachedLocalFilterModel,
      filterJSONString: cachedFilterJSONString,
    },
    defaultFilterModel: getDefaultFilterModel("number").filterModel,
  });

  const { columns } = useColumns(FILTER_TYPES.object);
  const sortModelString = sortModelToString(sortModel, columns);

  const {
    data,
    loadingState,
    fetchEntitiesData: fetchObjectsData,
    setFilterString,
    setOffset,
    limit,
    offset,
    setLimit,
    totalNumberOfRecords,
  } = useEntities("objects", {
    params: {
      ids: globalFilters?.value ?? null,
    },
    initialFilters: initialFilters.apiFilterString,
    order: sortModelString,
  });

  const fetchRecordsDataWithGlobalFilters = fetchDataWithOptionsAndFilters(
    fetchObjectsData
  )({
    limit,
    offset,
    order: sortModelString,
    params: {
      ids: globalFilters?.value ?? null,
    },
  });

  const fetchExportData = async (filterString, selectedColumnsString) => {
    const abortController = new AbortController();
    const searchParams = {
      filters: filterString,
      columns: selectedColumnsString,
      ids: globalFilters?.value ?? null,
    };
    const response = await exportRecords(
      API_ROUTE,
      searchParams,
      abortController
    );

    exportToFile(
      `export_${FILTER_TYPES.object}_overview_${moment().format(
        "DD-MM-YYYY"
      )}.csv`,
      response
    );

    return "Export done";
  };

  const { isAllowedTo } = useUserPermissionsContext();

  const isAllowedToCreate = isAllowedTo(UserRights.CREATE, FILTER_TYPES.object);
  const isAllowedToDelete = isAllowedTo(
    UserRights.SOFT_DELETE,
    FILTER_TYPES.object
  );
  const isAllowedToExport = isAllowedTo(UserRights.EXPORT_CSV);
  const isAllowedToInlineEdit = isAllowedTo(UserRights.INLINE_EDIT_OBJECT);

  const tableOptions = {};
  tableOptions.hasDelete = isAllowedToDelete;
  tableOptions.sortOrder = getSortOrder(FILTER_TYPES.object);
  tableOptions.hasInlineEdit = isAllowedToInlineEdit;

  return (
    <>
      <TableComponent
        {...{
          columns: transformColumns(sortColumns(columns)),
          transformColumns,
          loading: loadingState,
          rows: transformRows(data),
          refreshData: fetchRecordsDataWithGlobalFilters,
          renderCreateEntityButton: () => (
            <CreateItemButton
              to={ROUTES.object.create}
              isDisabled={!isAllowedToCreate}
              entity={FILTER_TYPES.object}
            />
          ),
          softDeleteEntry: softDelete({
            route: API_ROUTE,
            delete: true,
          }),
          hardDeleteEntry: hardDelete({
            route: API_ROUTE,
          }),
          restoreEntry: softDelete({
            route: API_ROUTE,
            delete: false,
          }),
          tableOptions,
          setFilters: setFilterString,
          setOffset,
          offset,
          setLimit,
          sortModel,
          setSortModel,
          numberOfRecords: totalNumberOfRecords,
          entityRoute: API_ROUTE,
          isAllowedToExport,
          entity: FILTER_TYPES.object,
          nestedEntity: false,
          initialFilterModel: initialFilters.grid,
          exportRecords: fetchExportData,
        }}
      />
    </>
  );
}
